import { useEffect, useRef, useState } from 'react';
import EraMediumFrame from '../EraMediumFrame';
import EraImagePlaceholder from '../EraImagePlaceholder';
import absoluteMediaUrl from '../../lib/absoluteMediaUrl';

/**
 * PictureShowHero — magazine cover for the chain step.
 *
 * Tall full-bleed image with gradient overlay, era-framed. Auto-advances every
 * 5s while `isPlaying`. Falls back to era-grammar placeholder when no usable
 * images are present.
 */
export default function PictureShowHero({ images, isPlaying, eraId = 'era9', subject = '', stepLabel }) {
  const [current, setCurrent] = useState(0);
  const [erroredKeys, setErroredKeys] = useState({});
  const intervalRef = useRef(null);

  // Single cleanup-on-every-render effect — guarantees no orphaned interval
  // even when `isPlaying` flips false (was a code-review gap in the inline
  // version).
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (isPlaying && images?.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrent((prev) => (prev + 1) % images.length);
      }, 5000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPlaying, images]);

  const usable = (images || []).filter((img) => img && img.url && !erroredKeys[img.url]);
  if (usable.length === 0) {
    return (
      <EraMediumFrame eraId={eraId} className="w-full" style={{ aspectRatio: '16 / 9' }}>
        <EraImagePlaceholder eraId={eraId} subject={subject} label={stepLabel || 'ARTIFACT PENDING'} />
      </EraMediumFrame>
    );
  }

  return (
    <EraMediumFrame eraId={eraId} className="w-full" style={{ aspectRatio: '16 / 9' }}>
      <div className="w-full h-full relative overflow-hidden" data-testid="picture-show">
        {usable.map((img, i) => (
          <div
            key={img.url || i}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: i === current ? 1 : 0 }}
          >
            <img
              src={absoluteMediaUrl(img.url)}
              alt={img.caption || subject || ''}
              loading="lazy"
              onError={() => setErroredKeys((prev) => ({ ...prev, [img.url]: true }))}
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(0.85) saturate(0.92)' }}
            />
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(transparent 55%, rgba(10,10,10,0.92))' }}
            />
            {img.caption && (
              <div className="absolute bottom-5 left-5 right-5">
                <p className="font-historical text-base md:text-lg text-[#e8d5a3] italic">{img.caption}</p>
                {img.source && (
                  <p className="font-mono text-[9px] text-[#52525B] mt-1">{img.source}</p>
                )}
              </div>
            )}
          </div>
        ))}
        {usable.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {usable.map((_, i) => (
              <div
                key={i}
                className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                style={{ background: i === current ? '#D4A84A' : 'rgba(255,255,255,0.2)' }}
              />
            ))}
          </div>
        )}
      </div>
    </EraMediumFrame>
  );
}
